import { BaseWsMessage } from 'interfaces/BaseWsMessage';
import { Store } from 'redux';
import { ScreenSharingService } from 'services/ScreenSharingService';
import {
    ClientScreenSharingController,
    SharingAPI,
} from 'src/clientSharing/ClientScreenSharingController';
import { RTCOptions } from 'src/clientSharing/sharing.types';
import { SharingStateManager } from 'src/clientSharing/SharingStateManager';
import { SharingWs } from 'src/clientSharing/SharingWs';
import { SharingActionType } from 'state/sharing/sharing.actions';

import { services } from '../container';
import { ServiceConfiguration } from '../ServiceConfiguration';

export class ScreenSharingServiceImpl implements ScreenSharingService {
    public screenSharing: ClientScreenSharingController = null;
    private ws: SharingWs = null;
    private widgetStore: Store = null;
    private moduleLoaded = false;
    private moduleLoading = false;
    private stateManager: SharingStateManager = null;

    constructor() {}

    public configure({ widgetStore }: ServiceConfiguration): void {
        this.widgetStore = widgetStore;
        widgetStore.subscribe(this.storeChanged);
    }

    storeChanged = () => {
        if (this.moduleLoaded) {
            this.screenSharing.storeChanged();
        } else {
            const sharingState = this.widgetStore.getState().sharing;
            if (sharingState.event === SharingActionType.CL_OPERATOR_REQUESTS_SHARING) {
                if (!this.moduleLoading) {
                    this.moduleLoading = true;
                    this.initSharingModule()
                        .then(() => {
                            this.moduleLoaded = true;
                            this.moduleLoading = false;
                            this.screenSharing.storeChanged();
                        })
                        .catch(() => {});
                }
            }
        }
    };

    initSharingModule = () => {
        const api: SharingAPI = {
            nav: navigator,
            Candidate: typeof RTCIceCandidate !== 'undefined' ? RTCIceCandidate : null,
            Description:
                typeof RTCSessionDescription !== 'undefined' ? RTCSessionDescription : null,
        };

        return import(/* webpackChunkName: "clientSharing" */ 'src/clientSharing')
            .then(({ default: module }) => {
                services.log.info('SHARING module loaded');
                this.ws = module.SharingWs.create(services.vo);
                this.stateManager = module.SharingStateManager.create(this.widgetStore);

                this.screenSharing = module.ClientScreenSharingController.create(
                    this,
                    this.stateManager,
                    this.ws,
                    services.log,
                    api,
                );
                return true;
            })
            .catch((error) => {
                services.log.error('An error occurred while loading ClientScreenSharingController');
                services.log.error(error);
                return Promise.reject(error);
            });
    };

    public handle = (action: BaseWsMessage) => {
        return this.screenSharing.handleWsEvents(action);
    };

    createConnection = (options: RTCOptions): RTCPeerConnection => new RTCPeerConnection(options);

    doesBrowserSupportSharing = () => {
        return (
            typeof RTCPeerConnection !== 'undefined' &&
            typeof RTCIceCandidate !== 'undefined' &&
            typeof RTCSessionDescription !== 'undefined' &&
            typeof navigator.mediaDevices?.getDisplayMedia !== 'undefined'
        );
    };
}
